import { useState, useEffect } from 'react'
import { Card, CardBody, CardHeader, Button, Chip } from '@heroui/react'

import { HealthCheckService } from '../services'
import type { HealthCheckResult } from '../services'

export function SystemStatus() {
  const [healthData, setHealthData] = useState<HealthCheckResult | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastChecked, setLastChecked] = useState<Date | null>(null)

  const checkHealth = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await HealthCheckService.checkSystemHealth()
      setHealthData(result)
      setLastChecked(new Date())
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to check system health'
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    checkHealth()
  }, [])

  const getOverallStatusColor = (overall: string) => {
    switch (overall) {
      case 'healthy':
        return 'success'
      case 'degraded':
        return 'warning'
      default:
        return 'danger'
    }
  }

  const getOverallStatusIcon = (overall: string) => {
    switch (overall) {
      case 'healthy':
        return '✅'
      case 'degraded':
        return '⚠️'
      default:
        return '❌'
    }
  }

  return (
    <Card>
      <CardHeader className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            System Status
          </h3>
          {healthData && (
            <Chip
              color={getOverallStatusColor(healthData.overall)}
              variant="flat"
              size="sm"
            >
              {getOverallStatusIcon(healthData.overall)}{' '}
              {healthData.overall.toUpperCase()}
            </Chip>
          )}
        </div>
        <Button
          size="sm"
          variant="flat"
          onPress={checkHealth}
          isLoading={isLoading}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </CardHeader>
      <CardBody>
        {error && (
          <div className="text-center text-red-600 dark:text-red-400 mb-4">
            {error}
          </div>
        )}

        {isLoading && !healthData && (
          <div className="text-center text-slate-600 dark:text-slate-400">
            Checking system health...
          </div>
        )}

        {healthData && (
          <>
            {/* Service Status Grid */}
            <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5 mb-4">
              {Object.entries(healthData.services).map(
                ([serviceName, status]) => (
                  <div key={serviceName} className="text-center">
                    <div
                      className={`text-2xl font-bold ${HealthCheckService.getStatusColor(status.status)}`}
                    >
                      {HealthCheckService.getStatusIcon(status.status)}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      {HealthCheckService.formatServiceName(serviceName)}
                    </div>
                    {status.error && (
                      <div
                        className="text-xs text-red-500 mt-1"
                        title={status.error}
                      >
                        Error
                      </div>
                    )}
                  </div>
                )
              )}
            </div>

            {/* Environment Info */}
            <div className="border-t pt-4 mt-4">
              <div className="flex justify-between items-center text-sm text-slate-600 dark:text-slate-400">
                <div className="flex gap-4">
                  <span>
                    Environment:{' '}
                    {healthData.environment.isEmulator
                      ? 'Emulator'
                      : 'Production'}
                  </span>
                  <span>Project: {healthData.environment.projectId}</span>
                </div>
                {lastChecked && (
                  <span>Last checked: {lastChecked.toLocaleTimeString()}</span>
                )}
              </div>
            </div>

            {/* Show detailed errors if any */}
            {Object.values(healthData.services).some((s) => s.error) && (
              <details className="mt-4">
                <summary className="cursor-pointer text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200">
                  View detailed errors
                </summary>
                <div className="mt-2 space-y-2 text-xs">
                  {Object.entries(healthData.services)
                    .filter(([, status]) => status.error)
                    .map(([serviceName, status]) => (
                      <div
                        key={serviceName}
                        className="bg-red-50 dark:bg-red-900/20 p-2 rounded"
                      >
                        <strong>
                          {HealthCheckService.formatServiceName(serviceName)}:
                        </strong>{' '}
                        {status.error}
                      </div>
                    ))}
                </div>
              </details>
            )}
          </>
        )}
      </CardBody>
    </Card>
  )
}
